import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'

const proofPoints = [
  { value: '02', label: 'active products' },
  { value: '01', label: 'internal workspace' },
  { value: '24/7', label: 'builder mode' },
]

const principles = [
  {
    index: '01',
    title: 'Useful first',
    accent: 'accent-green',
    copy: 'Every product has to earn its place in a real workflow before it earns more polish.',
  },
  {
    index: '02',
    title: 'Owned outcomes',
    accent: 'accent-coral',
    copy: 'We care about the entire loop: product, support, finance, distribution, and the quiet admin that keeps it alive.',
  },
  {
    index: '03',
    title: 'Ship with taste',
    accent: 'accent-blue',
    copy: 'Good software should feel immediate, trustworthy, and a little more alive than expected.',
  },
]

const portfolio = [
  {
    name: 'When Works',
    category: 'Scheduling',
    href: 'https://whenworks.cc',
    className: 'whenworks-card',
    icon: 'https://www.whenworks.cc/apple-icon',
    image: 'https://www.whenworks.cc/opengraph-image',
    imageAlt: 'When Works website preview',
    copy: 'Lightweight scheduling for finding the time that actually works, without turning coordination into a second meeting.',
  },
  {
    name: 'Mise Recipe Finder',
    category: 'Food tech',
    href: 'https://themiseapp.com',
    className: 'mise-card',
    icon: 'https://www.themiseapp.com/app-icon.png',
    image: 'https://www.themiseapp.com/og-image.jpg',
    imageAlt: 'Mise website preview',
    copy: 'A recipe companion for turning the ingredients you have into meals you want to make.',
  },
]

function HomePage() {
  const { isAuthenticated } = useAuth()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const currentYear = new Date().getFullYear()
  const appLink = isAuthenticated ? '/dashboard' : '/login'
  const appLabel = isAuthenticated ? 'Team console' : 'Team sign in'

  return (
    <div className="public-page studio-site">
      <header className="public-header">
        <div className="container">
          <Link to="/" className="logo">
            <div className="logo-icon">SSV</div>
            <span className="logo-text">Second Shift Ventures</span>
          </Link>

          <nav className="public-nav">
            <a href="#about">Thesis</a>
            <a href="#products">Portfolio</a>
            <a href="#team">Studio</a>
            <Link to={appLink} className="public-nav-internal">
              {appLabel}
            </Link>
          </nav>

          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? 'Close' : 'Menu'}
          </button>
        </div>
      </header>

      {mobileMenuOpen && (
        <div className="mobile-nav-overlay" onClick={() => setMobileMenuOpen(false)}>
          <nav className="mobile-nav" onClick={(e) => e.stopPropagation()}>
            <a href="#about" onClick={() => setMobileMenuOpen(false)}>Thesis</a>
            <a href="#products" onClick={() => setMobileMenuOpen(false)}>Portfolio</a>
            <a href="#team" onClick={() => setMobileMenuOpen(false)}>Studio</a>
            <Link to={appLink} className="public-nav-internal" onClick={() => setMobileMenuOpen(false)}>
              {appLabel}
            </Link>
          </nav>
        </div>
      )}

      <main>
        <section className="hero studio-hero">
          <div className="hero-visual" aria-hidden="true">
            <div className="hero-app-cloud">
              {portfolio.map((product, index) => (
                <div
                  className={`hero-app-card hero-app-card-${index + 1}`}
                  key={product.name}
                >
                  <img src={product.icon} alt="" loading="eager" />
                  <span>{product.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="container hero-shell">
            <div className="hero-content animate-fade-in-up">
              <div className="hero-kicker">
                <span></span>
                Venture studio and operating company
              </div>
              <h1>Second Shift Ventures</h1>
              <p>
                We build practical software businesses with a bias for useful products,
                crisp operations, and durable ownership.
              </p>
              <div className="hero-buttons">
                <a href="#products" className="btn btn-hero btn-hero-primary">
                  View portfolio
                </a>
              </div>
              <div className="hero-proof">
                {proofPoints.map((point) => (
                  <div key={point.label}>
                    <strong>{point.value}</strong>
                    <span>{point.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="about" className="section studio-section">
          <div className="container">
            <div className="section-header">
              <span className="section-eyebrow">Thesis</span>
              <h2>Small teams, real leverage, compounding systems.</h2>
              <p>
                We look for boringly important work, then build the tools, rituals,
                and distribution loops that turn it into something people keep using.
              </p>
            </div>

            <div className="principle-grid">
              {principles.map((principle) => (
                <article className={`principle-card ${principle.accent}`} key={principle.title}>
                  <div className="principle-index">{principle.index}</div>
                  <h3>{principle.title}</h3>
                  <p>{principle.copy}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="products" className="section portfolio-section">
          <div className="container">
            <div className="section-header">
              <span className="section-eyebrow">Portfolio</span>
              <h2>Products with clear jobs to do.</h2>
              <p>
                The portfolio is intentionally small and operated closely. Each company
                gets product work, finance discipline, and brand care from the same bench.
              </p>
            </div>

            <div className="portfolio-grid">
              {portfolio.map((product) => (
                <a
                  href={product.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`portfolio-card ${product.className}`}
                  key={product.name}
                >
                  <div className="portfolio-topline">
                    <span className="portfolio-chip">
                      <img src={product.icon} alt="" loading="lazy" />
                      {product.category}
                    </span>
                    <span className="portfolio-arrow">Visit</span>
                  </div>
                  <h3>{product.name}</h3>
                  <p>{product.copy}</p>
                  <div className="portfolio-preview">
                    <img className="portfolio-preview-image" src={product.image} alt={product.imageAlt} loading="lazy" />
                    <span className="portfolio-preview-badge" aria-hidden="true">
                      <img src={product.icon} alt="" loading="lazy" />
                      {product.name}
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section id="team" className="section team-section">
          <div className="container">
            <div className="team-panel">
              <div>
                <span className="section-eyebrow">Studio</span>
                <h2>Built after hours, operated like it matters.</h2>
              </div>
              <p>
                Second Shift is the shared home for product experiments, founder
                operations, and the unglamorous systems that let small bets become
                real businesses.
              </p>
            </div>
          </div>
        </section>
      </main>

      <footer className="public-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="logo">
                <div className="logo-icon">SSV</div>
                <span className="text-white">Second Shift Ventures</span>
              </div>
              <p>Practical software businesses, built with care.</p>
            </div>

            <div className="footer-links">
              <h4>Company</h4>
              <ul>
                <li><a href="#about">Thesis</a></li>
                <li><a href="#products">Portfolio</a></li>
                <li><a href="#team">Studio</a></li>
              </ul>
            </div>

            <div className="footer-links">
              <h4>Resources</h4>
              <ul>
                <li><a href="https://whenworks.cc" target="_blank" rel="noopener noreferrer">When Works</a></li>
                <li><a href="https://themiseapp.com" target="_blank" rel="noopener noreferrer">Mise</a></li>
              </ul>
            </div>

            <div className="footer-links">
              <h4>Legal</h4>
              <ul>
                <li><a href="#">Privacy</a></li>
                <li><a href="#">Terms</a></li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; {currentYear} Second Shift Ventures. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default HomePage
